import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { fromEvent, Subscription } from 'rxjs';
import * as moment from 'moment';

import { SENSEX_SYMBOL_KEY, START_TIME, INDEXSYMBOL, Utils } from '@app/_helpers';
import { 
    ConfigurationService, 
    MarketSocketService, 
    QuoteSocketUpdateI, 
    QuoteStoreService, 
    QUOTE_UPDATE_TYPE,
    Quote
} from '@app/_shared/providers';
import { MarketIDMap } from '@app/_shared/shared.enums';
import { TableColumnsService } from '@app/_shared/providers/table-columns/table-columns.service';
import { PinnedIndexSymbol } from './index-ticker.interfaces';
import { IndexWatchTutorial, IndexWatchOrder, isSwastika, isMastertrust } from '@app/_helpers/constants';
import { AlgoPermissions } from '../../../../utProto/webEnums_pb';
import { MbPlansService } from '@app/modules/trading/modules/mb-plans/providers/mb-plans.service';
import { IndexWatchDialogComponent } from './index-watch-dialog/index-watch-dialog.component';
import { debounceTime, take } from 'rxjs/operators';
import { AuthService } from '@app/modules/auth';
import { MixpanelService } from '@app/_shared/providers/mixpanel/mixpanel.service';
import { MIXPANEL_EVENTS } from '@app/_shared/providers/mixpanel/mixpanel.events';

@Component({
    selector: 'app-index-ticker',
    templateUrl: './index-ticker.component.html',
    styleUrls: ['./index-ticker.component.scss']
})
export class IndexTickerComponent implements OnInit, OnDestroy {
    indexSymbols: PinnedIndexSymbol[];
    private subscriptions = new Subscription();
    preventEvent = Utils.preventEvent;
    isFirstTimeUser: boolean;
    tutorialGif: string;
    DEFAULT_PRECISION = 2;

    maxVisibleIndexes: number = 4;
    // Constants for width calculation
    private readonly numeric_section_width = 214;
    private readonly symbol_char_width = 9;
    private readonly gap_between_symbols = 20;
    private readonly setting_button_width = 28;
    private readonly buffer_width = 60;
    /** Cached for session; BSEFO permission does not change mid-session (UT-8068). */
    private hasBsefoPermission: boolean | null = null;

    constructor(
        private configurationService: ConfigurationService,
        private marketService: MarketSocketService,
        private quoteService: QuoteStoreService,
        private tableColumnsService: TableColumnsService,
        private mbPlansService: MbPlansService,
        private dialog: MatDialog,
        private authService: AuthService,
        private mixpanel: MixpanelService
    ) {
        this.subscriptions.add(
            this.quoteService.getFeedSubject(QUOTE_UPDATE_TYPE.INDEX)
                .subscribe((quote: QuoteSocketUpdateI) => {
                    const key = this.quoteService.getMapKey(+MarketIDMap[quote.ExchangeId], quote.SecurityId);
                    const obj = this.indexSymbols?.find(el => el.key === key);
                    if (obj) {
                        this.updateMarketData(obj.value, quote.LastTradePrice, quote.ClosePrice);
                    }
                })
        );
        this.subscriptions.add(
            // UT-8068: Refresh displayed quotes after reconnect; socket replay handles re-subscribe.
            this.marketService.reconnected$.subscribe(() => {
                this.refreshIndexQuotes();
            })
        );
    }

    ngOnInit() {
        // Load index symbols from cache, fallback to remote config
        this.loadIndexSymbols();
        this.tutorialGif = this.configurationService.remoteConfigCacheObj?.TUTORIAL_GIF;
        if(!this.tutorialGif) {
            this.tutorialGif = "";
        }

        this.subscriptions.add(
            fromEvent(window, 'resize')
                .pipe(debounceTime(100))
                .subscribe(() => {
                    this.calculateMaxVisibleIndexes();
                })
        );
    }

    calculateMaxVisibleIndexes() {
        if (!this.indexSymbols || this.indexSymbols.length === 0) {
            this.maxVisibleIndexes = 1;
            return;
        }

        const availableWidth = window.innerWidth - this.setting_button_width - this.buffer_width;
        let totalWidth = 0;
        let count = 0;

        for(let i = 0; i < this.indexSymbols.length; i++) {
            const symbol = this.indexSymbols[i];
            const nameWidth = this.symbol_char_width * symbol.value.title.length;
            const cardWidth = nameWidth + this.numeric_section_width;
            const widthWithGap = count > 0 ? (count - 1) * this.gap_between_symbols + cardWidth : cardWidth;
            
            if (totalWidth + widthWithGap <= availableWidth) {
                totalWidth += widthWithGap;
                count++;
            } else {
                break; // Can't fit more
            }
        }

        // Ensure minimum is 1 and maximum is all symbols
        this.maxVisibleIndexes = Math.max(1, Math.min(count, this.indexSymbols.length));
    }

    loadIndexSymbols() {
        this.tableColumnsService.getPortfoliosLength(IndexWatchOrder).subscribe(
            async (res) => {
                if (res.data && res.data !== "") {
                    // Load from cache
                    try {
                        const cachedOrder = JSON.parse(res.data);
                        this.indexSymbols = cachedOrder;
                        this.calculateMaxVisibleIndexes();
                    } catch (error) {
                        console.error('Error parsing cached index watch order:', error);
                        this.loadDefaultIndexSymbols();
                    }
                } else {
                    // No cache found, load from remote config
                    this.loadDefaultIndexSymbols();
                }
                await this.filterAndSubscribe();
            },
            async (error) => {
                console.error('Error loading cached index watch order:', error);
                this.loadDefaultIndexSymbols();
                await this.filterAndSubscribe();
            }
        );
    }

    loadDefaultIndexSymbols() {
        this.indexSymbols = this.configurationService.remoteConfigCacheObj?.MARKET_INDICES;
        
        if (!this.configurationService.remoteConfigCacheObj.SENSEX_ALLOWED) {
            this.indexSymbols = this.indexSymbols?.filter(symbol => symbol.value.title !== INDEXSYMBOL.SENSEX);
        }
        this.calculateMaxVisibleIndexes();
    }

    async openIndexWatch() {
        const tutorialRes = await this.tableColumnsService.getPortfoliosLength(IndexWatchTutorial).pipe(take(1)).toPromise();
        this.isFirstTimeUser = (tutorialRes?.data ?? "") === "";

        await this.updateIndexPinnedSymbolsAndSubscribe();

        const dialogRef = this.dialog.open(IndexWatchDialogComponent, { //For reordering the index symbols
            width: '450px',
            maxWidth: '90vw',
            height: '520px',
            maxHeight: '80vh',
            backdropClass: 'backdrop-blur',
            panelClass: ['index-watch-dialog'],
            autoFocus: false,
            data: {
                indexSymbols: this.indexSymbols,
                isFirstTimeUser: this.isFirstTimeUser,
                tutorialGif: this.tutorialGif
            }
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result?.dismissTutorial) {
                this.dismissTutorial();
            }
            if (result?.saved) {
                this.saveIndexOrder();
            }
        });
        this.mixpanel.track(MIXPANEL_EVENTS.MANAGE_INDICES);
    }

    async updateIndexPinnedSymbolsAndSubscribe() {
        await this.filterAndSubscribe(true);
    }

    /**
     * UT-8068: Filter Sensex for multi-broker users without BSEFO permission, then subscribe.
     * Always subscribes even when getUserDetails() fails so indices are not left unsubscribed.
     */
    private async filterAndSubscribe(skipPin = false): Promise<void> {
        try {
            await this.filterIndexSymbolsByPermission();
        } catch (e) {
            console.error('Permission filter failed; subscribing all indices:', e);
        } finally {
            this.subscribeSymbols(skipPin);
        }
    }

    /**
     * UT-8068: On Swastika/Mastertrust, hide Sensex when user lacks BSEFO permission.
     * Previously this ran only when opening Manage Indices, leaving a frozen tile on initial load.
     */
    async filterIndexSymbolsByPermission(): Promise<void> {
        if (isSwastika || isMastertrust) {
            const hasBsefo = await this.resolveHasBsefoPermission();
            if (!hasBsefo) {
                this.indexSymbols = this.indexSymbols?.filter(el => el.key !== SENSEX_SYMBOL_KEY);
            }
        }
    }

    /** Resolve BSEFO permission once per session; reuses MbPlansService cache when already loaded. */
    private async resolveHasBsefoPermission(): Promise<boolean> {
        if (this.hasBsefoPermission !== null) {
            return this.hasBsefoPermission;
        }
        const details = this.mbPlansService.userDetails
            ?? await this.mbPlansService.getUserDetails().pipe(take(1)).toPromise();
        this.hasBsefoPermission = details.algo_permissions.includes(AlgoPermissions.WEBALGOPERMISSION_BSEFO);
        return this.hasBsefoPermission;
    }

    /** Refresh LTP/change from quote store after reconnect or initial load. Does not touch socket ref counts. */
    refreshIndexQuotes() {
        this.indexSymbols?.forEach(el => {
            const marketId = el.key.split('_')[0];
            const securityId = el.key.split('_')[1];
            this.quoteService.getFeedObj(+marketId, +securityId);
            this.marketService.getSingleUpdatedQuote(MarketIDMap[+marketId], +securityId).then((res: Quote) => {
                if (res) {
                    /**
                     * checking current time with the start time of market
                     * and if current time is before market time then using
                     * prevClosePrice and close price for calculating 
                     * change price and change percent.
                     */
                    const startDate = moment().set(START_TIME);
                    if (moment().isBefore(startDate)) {
                        this.updateMarketData(el.value, res.closePrice, res.prevClosePrice);
                    } else {
                        this.updateMarketData(el.value, res.LTP, res.closePrice);
                    }
                }
            });
        });
    }

    /**
     * @param skipPin When true (e.g. before Manage Indices), subscribe without incrementing pin count.
     * UT-8068: Pins are set once on initial load and released only on component destroy.
     */
    subscribeSymbols(skipPin = false) {
        this.refreshIndexQuotes();
        this.indexSymbols?.forEach(el => {
            const marketId = el.key.split('_')[0];
            const securityId = el.key.split('_')[1];
            this.marketService.subscribeSymbol({ market_id: +marketId, security_id: +securityId });
            // UT-8068: Pin prevents BSEFO components from fully unsubscribing Sensex (BSECM 1_1).
            if (!skipPin) {
                this.marketService.pinSymbolSubscription({ market_id: +marketId, security_id: +securityId });
            }
        });
    }

    updateMarketData(obj: any, ltp: number, closePrice: number) {
        if (ltp) {
            obj.ltp = Utils.applyPrecision(ltp);
            obj.closePrice = Utils.applyPrecision(closePrice);
            obj.changeInPrice = (ltp && obj.closePrice) ? Utils.applyPrecision(ltp) - obj.closePrice : null;
            obj.changePercent = obj.closePrice ? Utils.limitDecimalToDefault((obj.changeInPrice / obj.closePrice) * 100) : null;
        }
    }

    dismissTutorial() {
        const payload = { key: IndexWatchTutorial, data: "true" };
        this.tableColumnsService.setPortfoliosLength(payload).subscribe();
        this.isFirstTimeUser = false;
    }

    saveIndexOrder() {
        this.calculateMaxVisibleIndexes();
        // Separate from UT-8068 feed fix: persist only key, pinned, and title. Strip LTP fields so a
        // stale cached price does not flash on reload; live values are filled by refreshIndexQuotes on load.
        const symbolsToSave = this.indexSymbols?.map(el => ({
            key: el.key,
            pinned: el.pinned,
            value: {
                title: el.value.title,
                ltp: null,
                changeInPrice: null,
                changePercent: null,
                closePrice: null
            }
        }));
        const payload = { key: IndexWatchOrder, data: JSON.stringify(symbolsToSave) };
        this.tableColumnsService.setPortfoliosLength(payload).subscribe();
    }

    getClass(value: string) {
        if (value && (value.toString().charAt(value.length - 1) === '%')) {
            value = value.slice(0, -1);
        }
        if (+value > 0) {
            return 'text-green';
        } else if (+value < 0) {
            return 'text-negative';
        } else {
            return 'text-black';
        }
    }

    ngOnDestroy() {
        this.indexSymbols?.forEach(el => {
            const securityId = el.key.split('_')[1];
            const marketId = el.key.split('_')[0];
            // UT-8068: Release pin first so unSubscribeSymbol can send server unsub on logout.
            this.marketService.unpinSymbolSubscription({ market_id: +marketId, security_id: +securityId });
            this.marketService.unSubscribeSymbol({ market_id: +marketId, security_id: +securityId });
        });
        this.subscriptions.unsubscribe();
    }

    get indexTickerTopOffset(): number {
        const headerHeight = 50; // Header is 50px tall
        const alertHeight = 42; // alert is 42px tall
        let topOffset = 0;
        if (this.authService.isNewFeatureAlert) {
             topOffset = alertHeight;
        }
        return topOffset + headerHeight;
    }
}

